import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  IconButton,
  TextField,
  Typography,
  InputAdornment,
} from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import axios from 'src/utilities/http-client'
import {
  fetchLinkedServices,
  fetchSections,
  fetchCourses,
  fetchClasses,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectSections,
  selectSectionsStatus,
  selectCourses,
  selectCoursesStatus,
  selectClasses,
  selectClassesStatus,
  selectClassesByCourseID,
} from 'src/v3/create/create.slice'
import {
  AddIcon,
  CourseIcon,
  SearchIcon,
  EditIcon,
  DeleteIcon,
  PublishIcon,
  LinkedServiceIcon,
  SectionIcon,
  ImageIcon,
  ClockIcon,
  ClassIcon,
} from 'src/v3/core/icons'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import EditNoteIcon from '@mui/icons-material/EditNote'
import { selectRoles } from 'src/v3/settings/settings.slice'
import { Course, LinkedService, Section } from 'src/v3/core/types'
import Loading from 'src/v3/components/loading'
import ConfirmAction from 'src/v3/components/confirm-action'

interface CourseStats {
  classCount: number
  totalHours: number
}

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()

  let roles = useAppSelector(selectRoles)
  let isAdmin = roles.includes('admin')

  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let sections = useAppSelector(selectSections)
  let sectionsStatus = useAppSelector(selectSectionsStatus)
  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let classes = useAppSelector(selectClasses)
  let classesStatus = useAppSelector(selectClassesStatus)

  let [searchTerm, setSearchTerm] = useState<string>('')
  let [expandedServices, setExpandedServices] = useState<Set<string>>(new Set())
  let [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  let [confirm, setConfirm] = useState<string>('')
  let [publishing, setPublishing] = useState<string>('')
  let [deleting, setDeleting] = useState<string>('')

  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus])

  useEffect(() => {
    if (sectionsStatus === 'uninitialized') {
      dispatch(fetchSections())
    }
  }, [sectionsStatus])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  useEffect(() => {
    if (classesStatus === 'uninitialized') {
      dispatch(fetchClasses())
    }
  }, [classesStatus])

  // Filter courses based on search term
  let filteredCourses = courses.filter(
    (course) =>
      course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.displayName?.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  // Group courses by linkedService and section
  let organizedData = () => {
    let data: Record<string, Record<string, Course[]>> = {}

    // Add FOAMfrat for admins (sections with linkedServiceID = null/undefined/empty)
    if (isAdmin) {
      data['FOAMfrat'] = {}
      let foamfratSections = sections.filter(
        (section) => !section.linkedServiceID,
      )

      foamfratSections.forEach((section) => {
        data['FOAMfrat'][section.id] = filteredCourses.filter(
          (course) => course.sectionID === section.id,
        )
      })
    }

    // Add user's linked services
    linkedServices.forEach((service) => {
      data[service.id] = {}
      let serviceSections = sections.filter(
        (section) => section.linkedServiceID === service.id,
      )
      serviceSections.forEach((section) => {
        data[service.id][section.id] = filteredCourses.filter(
          (course) => course.sectionID === section.id,
        )
      })
    })

    return data
  }

  let toggleServiceExpanded = (serviceId: string) => {
    let newExpanded = new Set(expandedServices)
    if (newExpanded.has(serviceId)) {
      newExpanded.delete(serviceId)
    } else {
      newExpanded.add(serviceId)
    }
    setExpandedServices(newExpanded)
  }

  let toggleSectionExpanded = (sectionId: string) => {
    let newExpanded = new Set(expandedSections)
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId)
    } else {
      newExpanded.add(sectionId)
    }
    setExpandedSections(newExpanded)
  }

  let getCourseStats = (course: Course): CourseStats => {
    let courseClasses = classes.filter((c) => c.courseID === course.id)
    return {
      classCount: courseClasses.length,
      totalHours: course.length || 0,
    }
  }

  let getServiceName = (serviceId: string) => {
    if (serviceId === 'FOAMfrat') return 'FOAMfrat'
    return (
      linkedServices.find((ls) => ls.id === serviceId)?.name ||
      'Unknown Service'
    )
  }

  let getSectionName = (sectionId: string) => {
    return sections.find((s) => s.id === sectionId)?.name || 'Unknown Section'
  }

  let handleCreateCourse = (linkedServiceId?: string, sectionId?: string) => {
    let url = '/create/v4/course'
    if (linkedServiceId && sectionId) {
      url += `?linkedService=${linkedServiceId}&section=${sectionId}`
    }
    navigate(url)
  }

  let handleEditCourse = (courseId: string) => {
    navigate(`/create/v4/course/${courseId}`)
  }

  let handleTogglePublish = async (course: Course) => {
    let action = course.published === 'published' ? 'unpublish' : 'publish'

    setPublishing(course.id)
    try {
      await axios.put('/api/v2/create/publish-course', {
        id: course.id,
        newPublicationStatus: `${action}ed`,
      })
      await dispatch(fetchCourses())
    } catch (err: any) {
      console.error('Error updating course status:', err)
    } finally {
      setPublishing('')
    }
  }

  let handleDeleteCourse = async (course: Course) => {
    setDeleting(course.id)
    try {
      await axios.delete(`/api/v2/create/delete-course?courseID=${course.id}`)
      await dispatch(fetchCourses())
    } catch (err: any) {
      console.error('Error deleting course:', err)
      throw err
    } finally {
      setDeleting('')
    }
  }

  let handleDeleteSection = async (sectionId: string) => {
    try {
      await axios.delete(`/api/v2/create/delete-section?sectionID=${sectionId}`)
      await dispatch(fetchSections())
    } catch (err: any) {
      console.error('Error deleting section:', err)
      throw err
    }
  }

  let organized = organizedData()

  return (
    <Container
      maxWidth="xl"
      sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Create & Manage Courses
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 3 }}>
          Organize your courses by section and create guided learning
          experiences
        </Typography>

        {/* Quick Actions & Search */}
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 2, sm: 3 },
            mb: 4,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
          }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => handleCreateCourse()}
            sx={{ flexShrink: 0 }}
          >
            Create New Course
          </Button>

          <TextField
            placeholder="Search courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: { xs: 'auto', sm: 300 },
              width: { xs: '100%', sm: 'auto' },
            }}
          />
        </Box>
      </Box>

      {/* Course Organization */}
      <Box>
        {Object.keys(organized).length === 0 ? (
          <Alert severity="info" sx={{ mb: 4 }}>
            No courses found. Create your first course to get started.
          </Alert>
        ) : (
          Object.entries(organized).map(([serviceId, serviceSections]) => {
            let serviceName = getServiceName(serviceId)
            let isServiceExpanded = expandedServices.has(serviceId)
            let totalCourses = Object.values(serviceSections).flat().length

            return (
              <Card key={serviceId} variant="outlined" sx={{ mb: 3 }}>
                <CardContent sx={{ pb: 2 }}>
                  {/* Service Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      py: 1,
                      flexWrap: { xs: 'wrap', md: 'nowrap' },
                      gap: { xs: 2, md: 0 },
                    }}
                    onClick={() => toggleServiceExpanded(serviceId)}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1, sm: 2 },
                        flexWrap: 'wrap',
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <LinkedServiceIcon color="primary" />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                          minWidth: 0,
                        }}
                      >
                        {serviceName}
                      </Typography>
                      <Chip
                        label={`${totalCourses} courses`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexShrink: 0,
                        width: { xs: '100%', md: 'auto' },
                        justifyContent: { xs: 'space-between', md: 'flex-end' },
                        order: { xs: 2, md: 0 },
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleCreateCourse(serviceId)
                        }}
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          px: { xs: 1, sm: 2 },
                        }}
                      >
                        Create Course
                      </Button>
                      <IconButton>
                        {isServiceExpanded ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Service Sections */}
                  <Collapse in={isServiceExpanded}>
                    <Box sx={{ ml: 4, mt: 2 }}>
                      {Object.entries(serviceSections).map(
                        ([sectionId, sectionCourses]) => {
                          let sectionName = getSectionName(sectionId)
                          let isSectionExpanded =
                            expandedSections.has(sectionId)

                          return (
                            <Box key={sectionId} sx={{ mb: 3 }}>
                              {/* Section Header */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  cursor: 'pointer',
                                  py: 1,
                                  px: { xs: 1, sm: 2 },
                                  backgroundColor: 'background.default',
                                  borderRadius: 1,
                                  flexWrap: { xs: 'wrap', md: 'nowrap' },
                                  gap: { xs: 1, md: 0 },
                                }}
                                onClick={() => toggleSectionExpanded(sectionId)}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: { xs: 1, sm: 2 },
                                    flexWrap: 'wrap',
                                    minWidth: 0,
                                    flex: 1,
                                  }}
                                >
                                  <SectionIcon color="action" />
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: { xs: '0.875rem', sm: '1rem' },
                                      minWidth: 0,
                                    }}
                                  >
                                    {sectionName}
                                  </Typography>
                                  <Chip
                                    label={`${sectionCourses.length} courses`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                </Box>

                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    flexShrink: 0,
                                    width: { xs: '100%', md: 'auto' },
                                    justifyContent: {
                                      xs: 'space-between',
                                      md: 'flex-end',
                                    },
                                    order: { xs: 2, md: 0 },
                                  }}
                                >
                                  <Button
                                    variant="text"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleCreateCourse(serviceId, sectionId)
                                    }}
                                    sx={{
                                      fontSize: {
                                        xs: '0.75rem',
                                        sm: '0.875rem',
                                      },
                                      px: { xs: 1, sm: 2 },
                                    }}
                                  >
                                    Create Course
                                  </Button>
                                  {sectionCourses.length === 0 && (
                                    <IconButton
                                      size="small"
                                      color="error"
                                      title="Delete Empty Section"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setConfirm(
                                          `delete-section-${sectionId}`,
                                        )
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                  <IconButton size="small">
                                    {isSectionExpanded ? (
                                      <ExpandLessIcon />
                                    ) : (
                                      <ExpandMoreIcon />
                                    )}
                                  </IconButton>
                                </Box>
                              </Box>

                              {/* Section Courses - Using Tailwind Grid */}
                              <Collapse in={isSectionExpanded}>
                                <div className="mt-4 ml-0 sm:ml-4">
                                  {sectionCourses.length === 0 ? (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                      No courses in this section yet.
                                    </Alert>
                                  ) : (
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                      {sectionCourses.map((course) => {
                                        let stats = getCourseStats(course)

                                        return (
                                          <Card
                                            key={course.id}
                                            variant="outlined"
                                            className="flex flex-col h-full"
                                          >
                                            {/* Course Thumbnail */}
                                            <Box
                                              sx={{
                                                position: 'relative',
                                                width: '100%',
                                                paddingBottom: '56.25%', // 16:9 aspect ratio
                                                bgcolor: 'grey.100',
                                                overflow: 'hidden',
                                              }}
                                            >
                                              {course.imageName ? (
                                                <img
                                                  src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${course.imageName}`}
                                                  alt={
                                                    course.displayName ||
                                                    course.name
                                                  }
                                                  style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                  }}
                                                />
                                              ) : (
                                                <Box
                                                  sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 1,
                                                    bgcolor: 'black',
                                                    color: 'text.secondary',
                                                  }}
                                                >
                                                  <ImageIcon
                                                    sx={{ fontSize: 32 }}
                                                  />
                                                  <Typography variant="caption">
                                                    No Image
                                                  </Typography>
                                                </Box>
                                              )}
                                              {/* Status Badge */}
                                              <Chip
                                                icon={
                                                  course.published ===
                                                  'published' ? (
                                                    <PublishIcon />
                                                  ) : (
                                                    <EditNoteIcon />
                                                  )
                                                }
                                                label={
                                                  course.published ===
                                                  'published'
                                                    ? 'Published'
                                                    : 'Draft'
                                                }
                                                size="small"
                                                color={
                                                  course.published ===
                                                  'published'
                                                    ? 'success'
                                                    : 'warning'
                                                }
                                                variant="filled"
                                                clickable
                                                disabled={
                                                  publishing === course.id
                                                }
                                                onClick={(e) => {
                                                  e.stopPropagation()
                                                  setConfirm(
                                                    `publish-${course.id}`,
                                                  )
                                                }}
                                                sx={{
                                                  position: 'absolute',
                                                  top: 8,
                                                  right: 8,
                                                  cursor: 'pointer',
                                                }}
                                              />
                                            </Box>

                                            <CardContent
                                              sx={{ flexGrow: 1, pb: 1 }}
                                            >
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 1,
                                                  mb: 1,
                                                }}
                                              >
                                                <CourseIcon color="primary" />
                                                <Typography
                                                  variant="h6"
                                                  component="div"
                                                  sx={{
                                                    fontSize: '1rem',
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {course.displayName ||
                                                    course.name}
                                                </Typography>
                                              </Box>

                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                  mb: 2,
                                                  minHeight: '2.5em',
                                                }}
                                              >
                                                {course.name !==
                                                course.displayName
                                                  ? course.name
                                                  : 'Course description...'}
                                              </Typography>

                                              <div className="flex gap-4 mb-4">
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                  }}
                                                >
                                                  <ClassIcon
                                                    sx={{ fontSize: 16 }}
                                                    color="action"
                                                  />
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                  >
                                                    {stats.classCount}{' '}
                                                    {stats.classCount === 1
                                                      ? 'class'
                                                      : 'classes'}
                                                  </Typography>
                                                </Box>
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                  }}
                                                >
                                                  <ClockIcon
                                                    sx={{ fontSize: 16 }}
                                                    color="action"
                                                  />
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                  >
                                                    {stats.totalHours} hours
                                                  </Typography>
                                                </Box>
                                              </div>
                                            </CardContent>

                                            <div className="flex flex-col items-stretch justify-between gap-2 p-4 pt-0 sm:flex-row sm:items-center">
                                              <Button
                                                size="small"
                                                startIcon={<EditIcon />}
                                                onClick={() =>
                                                  handleEditCourse(course.id)
                                                }
                                                sx={{
                                                  fontSize: {
                                                    xs: '0.75rem',
                                                    sm: '0.875rem',
                                                  },
                                                  order: { xs: 2, sm: 1 },
                                                }}
                                              >
                                                Edit Course
                                              </Button>

                                              <IconButton
                                                size="small"
                                                color="error"
                                                title="Delete Course"
                                                disabled={
                                                  deleting === course.id
                                                }
                                                onClick={(e) => {
                                                  e.stopPropagation()
                                                  setConfirm(
                                                    `delete-${course.id}`,
                                                  )
                                                }}
                                                sx={{
                                                  alignSelf: {
                                                    xs: 'flex-end',
                                                    sm: 'center',
                                                  },
                                                  order: { xs: 1, sm: 2 },
                                                }}
                                              >
                                                <DeleteIcon />
                                              </IconButton>
                                            </div>
                                          </Card>
                                        )
                                      })}
                                    </div>
                                  )}
                                </div>
                              </Collapse>
                            </Box>
                          )
                        },
                      )}
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            )
          })
        )}
      </Box>

      {/* Publish/Unpublish Confirmation Dialog */}
      {confirm.startsWith('publish-') && (
        <ConfirmAction
          executeAction={() => {
            let courseId = confirm.replace('publish-', '')
            let course = courses.find((c) => c.id === courseId)
            if (course) {
              return handleTogglePublish(course)
            }
            return Promise.resolve()
          }}
          open={confirm}
          setOpen={setConfirm}
          openText={confirm}
          title={(() => {
            let courseId = confirm.replace('publish-', '')
            let course = courses.find((c) => c.id === courseId)
            return course?.published === 'published'
              ? 'Unpublish Course'
              : 'Publish Course'
          })()}
          text={(() => {
            let courseId = confirm.replace('publish-', '')
            let course = courses.find((c) => c.id === courseId)
            let action =
              course?.published === 'published' ? 'unpublish' : 'publish'
            return `Are you sure you want to ${action} "${course?.displayName || course?.name}"?`
          })()}
        />
      )}

      {/* Delete Course Confirmation Dialog */}
      {confirm.startsWith('delete-') &&
        !confirm.startsWith('delete-section-') && (
          <ConfirmAction
            executeAction={() => {
              let courseId = confirm.replace('delete-', '')
              let course = courses.find((c) => c.id === courseId)
              if (course) {
                return handleDeleteCourse(course)
              }
              return Promise.resolve()
            }}
            open={confirm}
            setOpen={setConfirm}
            openText={confirm}
            title="Delete Course"
            text={(() => {
              let courseId = confirm.replace('delete-', '')
              let course = courses.find((c) => c.id === courseId)
              return `Are you sure you want to delete "${course?.displayName || course?.name}"? This action cannot be undone.`
            })()}
          />
        )}

      {/* Delete Section Confirmation Dialog */}
      {confirm.startsWith('delete-section-') && (
        <ConfirmAction
          executeAction={() => {
            let sectionId = confirm.replace('delete-section-', '')
            return handleDeleteSection(sectionId)
          }}
          open={confirm}
          setOpen={setConfirm}
          openText={confirm}
          title="Delete Empty Section"
          text={(() => {
            let sectionId = confirm.replace('delete-section-', '')
            let sectionName = getSectionName(sectionId)
            return `Are you sure you want to delete the empty section "${sectionName}"? This action cannot be undone.`
          })()}
        />
      )}

      <Loading
        entities={[
          linkedServicesStatus,
          sectionsStatus,
          coursesStatus,
          classesStatus,
        ]}
      />
    </Container>
  )
}
